import "../global.css";

import { Stack, SplashScreen } from "expo-router";
import { useEffect, useState } from "react";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { SupabaseProvider } from "@/context/AuthProvider";
import { TRPCReactProvider } from "@/trpc/react";
import { PortalHost } from "@/lib/rn-primatives/portal";
import { Alert, LogBox, Platform, StatusBar as ReactNativeStatusBar } from "react-native";
import { StatusBar } from "expo-status-bar";
import Toast from 'react-native-toast-message';
import { toastConfig } from '@/components/ui/toast-config';
import { MenuProvider } from "react-native-popup-menu";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { PushRegistrationProvider } from '@/context/PushRegistrationProvider';

// Only import native modules on native platforms
let OneSignal: any;
let Constants: any;
let Updates: any;

// Only import native-specific modules on native platforms
if (Platform.OS !== 'web') {
  // Dynamic imports to avoid web errors
  Constants = require('expo-constants').default;
  Updates = require('expo-updates');

  // Import OneSignal only for native platforms
  const OneSignalModule = require('react-native-onesignal');
  OneSignal = OneSignalModule.OneSignal;
}

const CHECK_UPDATE_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from "expo-router";

export const unstable_settings = {
  initialRouteName: "(auth)",
};

// uncomment to ignore all logs
LogBox.ignoreAllLogs(true);

SplashScreen.preventAutoHideAsync();

// Only initialize OneSignal on native platforms
if (Platform.OS !== 'web' && OneSignal) {
  const isRunningInExpoGo = Constants.appOwnership === 'expo';
  if (!isRunningInExpoGo) {
    // Get LogLevel from the same module where OneSignal was imported
    const { LogLevel } = require('react-native-onesignal');

    OneSignal.initialize(Constants.expoConfig?.extra?.oneSignalAppId);
    // NOTE: do NOT call OneSignal.Notifications.requestPermission() here.
    // On a fresh install OneSignal launches a native PermissionsActivity to
    // bridge to the OS permission dialog. Doing that during React Native
    // root-layout init races with the Fabric layout commit and crashes the
    // app with a YogaLayoutableShadowNode assertion. Permission is requested
    // only from the in-app banner / /enable-notifications guide, where the
    // RN tree is already mounted and stable.
  }
}

export default function RootLayout() {
  useEffect(() => {
    // Set status bar to light content (white text) for dark headers
    if (Platform.OS === 'android') {
      // targetSdk 36 forces edge-to-edge, where setBackgroundColor/setTranslucent
      // are no-ops. Only the content style and visibility still apply.
      ReactNativeStatusBar.setBarStyle('light-content');
      ReactNativeStatusBar.setHidden(false);
    }

    // HACK: Hide splash screen after 1 second to hide initial routing animation.
    setTimeout(() => {
      SplashScreen.hideAsync();
    }, 1000);
  }, []);

  return <RootLayoutNav />;
}

function RootLayoutNav() {
  // Only check for updates on native platforms
  const updatesInfo = Platform.OS !== 'web' && Updates ?
    Updates.useUpdates() :
    { isUpdateAvailable: false, lastCheckForUpdateTimeSinceRestart: null };

  const { isUpdateAvailable, lastCheckForUpdateTimeSinceRestart, currentlyRunning } = updatesInfo;

  // Handle OneSignal re-registration after OTA updates
  useEffect(() => {
    if (Platform.OS === 'web' || !OneSignal || !currentlyRunning) {
      return;
    }

    // If we're running an update (not embedded launch), re-register OneSignal
    if (!currentlyRunning.isEmbeddedLaunch) {
      try {
        // Re-initialize OneSignal after OTA update to ensure notifications work
        OneSignal.initialize(Constants.expoConfig?.extra?.oneSignalAppId);
        // Do not call requestPermission here either; see note in the
        // module-init block above. The in-app banner / guide handle it safely.

        // Note: OneSignal.login() will be re-issued by AuthProvider's user-id effect once the session is rehydrated.
        console.log('OneSignal re-initialized after OTA update');
      } catch (error) {
        console.error('Failed to re-initialize OneSignal after update:', error);
      }
    }
  }, [currentlyRunning?.isEmbeddedLaunch]);

  useEffect(() => {
    // Skip update checks on web
    if (Platform.OS === 'web' || !Updates) {
      return;
    }

    const checkForUpdates = async () => {
      try {
        const update = await Updates.checkForUpdateAsync();
        if (update.isAvailable) {
          await Updates.fetchUpdateAsync();
          Alert.alert(
            'Crew app update available',
            'An important new update is available. Would you like to update now?',
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Update', onPress: () => Updates.reloadAsync() },
            ],
            { cancelable: false }
          );
        }
      } catch (e) {
        console.error(e);
      }
    };

    // Initial check
    if (!lastCheckForUpdateTimeSinceRestart) {
      checkForUpdates();
    }

    // Periodic check
    const interval = setInterval(() => {
      if (!lastCheckForUpdateTimeSinceRestart) {
        checkForUpdates();
      } else {
        const timeSinceLastCheck = Date.now() - lastCheckForUpdateTimeSinceRestart.getTime();
        const oneHour = 5 * 60 * 1000;

        if (timeSinceLastCheck >= oneHour) {
          checkForUpdates();
        }
      }
    }, 5 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [lastCheckForUpdateTimeSinceRestart]);

  return (
    <>
      <StatusBar style="light" />
      <SupabaseProvider>
        <TRPCReactProvider>
          <PushRegistrationProvider>
            <SafeAreaProvider>
            <MenuProvider>
              <KeyboardProvider>
                <Stack
                  screenOptions={{
                    headerShown: false,
                    headerBackButtonDisplayMode: 'minimal',
                  }}
                >
                  <Stack.Screen name="(auth)" />
                  <Stack.Screen name="(onboarding)" />
                  <Stack.Screen name="(app)" />
                  <Stack.Screen name="(account)" />
                  <Stack.Screen
                    name="enable-notifications"
                    options={{ presentation: 'modal', animation: 'slide_from_bottom' }}
                  />
                </Stack>
              </KeyboardProvider>
            </MenuProvider>
            </SafeAreaProvider>
          </PushRegistrationProvider>
        </TRPCReactProvider>
      </SupabaseProvider>
      <PortalHost />
      <Toast config={toastConfig} />
    </>
  );
}